import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

// Verified icon is a fixed-size SVG (24px icon + ~4px gap from Inline space)
const VERIFIED_ICON_WIDTH = 28;
// Lozenge has ~8px horizontal padding on each side + ~4px gap from Inline space
const LOZENGE_PADDING = 20;

const RESIZE_DEBOUNCE_MS = 50;

type TruncationConfig = {
	hasVerified: boolean;
	hasArchived: boolean;
	archivedText: string;
};

// Reusable canvas for text measurement (created lazily, cached)
let measureCanvas: HTMLCanvasElement | null = null;

/**
 * Detects if an element's content would be truncated when badges are present.
 *
 * Instead of detecting current truncation (which causes feedback loops when badges
 * toggle), we calculate whether text + badges would fit in the available space.
 * Uses Canvas API to measure badge text width without rendering duplicate elements.
 *
 * Features:
 * - SSR safe (returns false during server-side rendering)
 * - Debounced resize handling to prevent excessive calculations
 * - Cached canvas instance for text measurement
 */
function useTruncationDetection({ hasVerified, hasArchived, archivedText }: TruncationConfig): {
	elementRef: RefObject<HTMLElement>;
	isTruncated: boolean;
} {
	const elementRef = useRef<HTMLElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);
	const lastContainerWidth = useRef<number>(0);
	const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const hasBadges = hasVerified || hasArchived;

	useLayoutEffect(() => {
		if (!hasBadges) {
			setIsTruncated(false);
			return;
		}

		// SSR safety check
		if (typeof document === 'undefined') {
			return;
		}

		const element = elementRef.current;
		// Get stable container (grandparent - Stack) that doesn't change with badges
		const container = element?.parentElement?.parentElement;
		if (!element || !container) {
			return;
		}

		let badgeWidth = 0;
		if (hasVerified) {
			badgeWidth += VERIFIED_ICON_WIDTH;
		}
		if (hasArchived && archivedText) {
			const computedStyle = getComputedStyle(element);
			const lozengeFont = `600 11px ${computedStyle.fontFamily}`;
			const textWidth = measureTextWidth(archivedText, lozengeFont);
			badgeWidth += textWidth + LOZENGE_PADDING;
		}

		const checkTruncation = () => {
			const containerWidth = container.clientWidth;

			// Only recheck if container width actually changed
			if (containerWidth === lastContainerWidth.current && lastContainerWidth.current !== 0) {
				return;
			}
			lastContainerWidth.current = containerWidth;

			const range = document.createRange();
			range.selectNodeContents(element);
			const textWidth = range.getBoundingClientRect().width;

			const wouldTruncateWithBadges = textWidth + badgeWidth > containerWidth;

			setIsTruncated(wouldTruncateWithBadges);
		};

		const debouncedCheckTruncation = () => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
			debounceTimeoutRef.current = setTimeout(checkTruncation, RESIZE_DEBOUNCE_MS);
		};

		const resizeObserver = new ResizeObserver(debouncedCheckTruncation);
		resizeObserver.observe(container);

		checkTruncation();

		return () => {
			resizeObserver.disconnect();
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, [hasBadges, hasVerified, hasArchived, archivedText]);

	return { elementRef, isTruncated };
}

/**
 * Measures text width using the Canvas API.
 * Reuses a single canvas instance for performance.
 * Returns 0 during SSR when document is unavailable.
 */
function measureTextWidth(text: string, font: string): number {
	// SSR safety check
	if (typeof document === 'undefined') {
		return 0;
	}
	if (!measureCanvas) {
		measureCanvas = document.createElement('canvas');
	}
	const context = measureCanvas.getContext('2d');
	if (!context) {
		return 0;
	}
	context.font = font;
	return context.measureText(text).width;
}

export { useTruncationDetection };
